import React, { useState, useEffect } from 'react';
import {
    Card, Table, Button, Input, InputNumber, Typography, message, Row, Col,
    Tag, Select, Modal, Form, DatePicker, Divider, Statistic, Space
} from 'antd';
import {
    RollbackOutlined, SearchOutlined, PlusOutlined, DollarOutlined,
    ExclamationCircleOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import api from '../../services/api';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const REASONS = ['Damaged', 'Expired', 'Wrong Item', 'Customer Request', 'Defective', 'Other'];

const Returns = () => {
    const { t } = useTranslation(['pharmacy', 'common']);
    const [returns, setReturns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [dateRange, setDateRange] = useState(null);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });

    // New return modal
    const [modalVisible, setModalVisible] = useState(false);
    const [saleSearch, setSaleSearch] = useState('');
    const [foundSale, setFoundSale] = useState(null);
    const [saleLoading, setSaleLoading] = useState(false);
    const [returnItems, setReturnItems] = useState([]);
    const [refundMethod, setRefundMethod] = useState('Cash');
    const [notes, setNotes] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchReturns();
    }, [dateRange]);

    const fetchReturns = async (page = 1) => {
        try {
            setLoading(true);
            let url = `/pharmacy/returns?page=${page}&limit=${pagination.pageSize}`;
            if (dateRange && dateRange[0] && dateRange[1]) {
                url += `&startDate=${dateRange[0].format('YYYY-MM-DD')}&endDate=${dateRange[1].format('YYYY-MM-DD')}`;
            }
            const res = await api.get(url);
            setReturns(res.data.data || []);
            setPagination(prev => ({
                ...prev,
                current: page,
                total: res.data.total || res.data.count || 0
            }));
        } catch (error) {
            console.error('Error fetching returns:', error);
        } finally {
            setLoading(false);
        }
    };

    const lookupSale = async () => {
        if (!saleSearch.trim()) {
            message.warning(t('pharmacy:messages.enterInvoice'));
            return;
        }
        try {
            setSaleLoading(true);
            // Search sales by saleNumber
            const res = await api.get(`/pharmacy/sales?page=1&limit=5`);
            const allSales = res.data.data || [];
            const match = allSales.find(s =>
                s.saleNumber?.toLowerCase() === saleSearch.trim().toLowerCase()
            );

            if (!match) {
                message.error(t('pharmacy:messages.saleNotFound'));
                setFoundSale(null);
                setReturnItems([]);
                return;
            }

            if (match.paymentStatus === 'Refunded') {
                message.warning(t('pharmacy:messages.alreadyRefunded'));
                setFoundSale(null);
                setReturnItems([]);
                return;
            }

            setFoundSale(match);
            // Pre-fill return items with 0 quantity
            setReturnItems(match.items.map(item => ({
                pharmacyItemId: item.pharmacyItemId?._id || item.pharmacyItemId,
                itemName: item.itemName,
                maxQty: item.quantity,
                unitPrice: item.unitPrice,
                quantityReturned: 0,
                reason: 'Customer Request'
            })));
        } catch (error) {
            message.error('Error looking up sale');
        } finally {
            setSaleLoading(false);
        }
    };

    const updateReturnItem = (index, field, value) => {
        const updated = [...returnItems];
        updated[index][field] = value;
        setReturnItems(updated);
    };

    const totalRefund = returnItems.reduce(
        (sum, item) => sum + (item.quantityReturned * item.unitPrice), 0
    );
    const hasReturnItems = returnItems.some(item => item.quantityReturned > 0);

    const handleSubmitReturn = async () => {
        const itemsToReturn = returnItems.filter(item => item.quantityReturned > 0);

        if (itemsToReturn.length === 0) {
            message.error(t('pharmacy:messages.selectItemToReturn'));
            return;
        }

        try {
            setSaving(true);
            await api.post('/pharmacy/returns', {
                saleId: foundSale._id,
                items: itemsToReturn.map(item => ({
                    pharmacyItemId: item.pharmacyItemId,
                    itemName: item.itemName,
                    quantityReturned: item.quantityReturned,
                    reason: item.reason
                })),
                refundMethod,
                notes
            });

            message.success(t('pharmacy:messages.returnProcessed'));
            setModalVisible(false);
            resetModal();
            fetchReturns();
        } catch (error) {
            message.error(error.response?.data?.message || t('pharmacy:messages.returnFailed'));
        } finally {
            setSaving(false);
        }
    };

    const resetModal = () => {
        setSaleSearch('');
        setFoundSale(null);
        setReturnItems([]);
        setRefundMethod('Cash');
        setNotes('');
    };

    const filteredReturns = search
        ? returns.filter(r =>
            (r.returnNumber || '').toLowerCase().includes(search.toLowerCase()) ||
            (r.saleNumber || '').toLowerCase().includes(search.toLowerCase()) ||
            (r.patientId?.name || '').toLowerCase().includes(search.toLowerCase())
        )
        : returns;

    const totalRefunded = filteredReturns.reduce((sum, r) => sum + (r.totalRefundAmount || 0), 0);

    const columns = [
        {
            title: t('pharmacy:returns.returnNumber'),
            dataIndex: 'returnNumber',
            key: 'returnNumber',
            render: (text) => <Text strong style={{ color: '#ff4d4f' }}>{text}</Text>,
            width: 140
        },
        {
            title: t('pharmacy:common.date'),
            dataIndex: 'returnDate',
            key: 'returnDate',
            render: (date) => dayjs(date).format('YYYY-MM-DD HH:mm'),
            width: 150,
            sorter: (a, b) => new Date(a.returnDate) - new Date(b.returnDate)
        },
        {
            title: t('pharmacy:returns.saleInvoice'),
            dataIndex: 'saleNumber',
            key: 'saleNumber',
            render: (text) => <Text type="secondary">{text}</Text>,
            width: 140
        },
        {
            title: t('pharmacy:common.patient'),
            dataIndex: 'patientId',
            key: 'patient',
            render: (p) => p?.name || <Text type="secondary">{t('pharmacy:common.walkIn')}</Text>,
            width: 140
        },
        {
            title: t('pharmacy:common.items'),
            dataIndex: 'items',
            key: 'items',
            render: (items) => items?.length || 0,
            width: 70,
            align: 'center'
        },
        {
            title: t('pharmacy:common.currency'),
            dataIndex: 'currency',
            key: 'currency',
            width: 80,
            render: (c) => <Tag>{c}</Tag>
        },
        {
            title: t('pharmacy:returns.refundAmount'),
            dataIndex: 'totalRefundAmount',
            key: 'total',
            width: 140,
            render: (amount, record) => (
                <Text strong style={{ color: '#ff4d4f' }}>
                    {record.currency === 'IQD' ? `IQD ${amount?.toLocaleString()}` : `$${amount?.toFixed(2)}`}
                </Text>
            )
        },
        {
            title: t('pharmacy:returns.method'),
            dataIndex: 'refundMethod',
            key: 'refundMethod',
            width: 110
        },
        {
            title: t('pharmacy:common.status'),
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (status) => (
                <Tag color={status === 'Completed' ? 'green' : 'red'}>{t(`pharmacy:common.paymentStatuses.${status}`, status)}</Tag>
            )
        },
        {
            title: t('pharmacy:returns.processedBy'),
            dataIndex: 'processedBy',
            key: 'processedBy',
            render: (u) => u?.name || '-',
            width: 110
        }
    ];

    const expandedRowRender = (record) => (
        <Table
            dataSource={record.items}
            rowKey={(item, idx) => `${item.pharmacyItemId}-${idx}`}
            pagination={false}
            size="small"
            columns={[
                { title: t('pharmacy:common.item'), dataIndex: 'itemName', key: 'itemName' },
                { title: t('pharmacy:returns.qtyReturned'), dataIndex: 'quantityReturned', key: 'qty', width: 100 },
                {
                    title: t('pharmacy:common.unitPrice'), dataIndex: 'unitPrice', key: 'unitPrice', width: 110,
                    render: (v) => record.currency === 'IQD' ? `IQD ${v?.toLocaleString()}` : `$${v?.toFixed(2)}`
                },
                {
                    title: t('pharmacy:returns.refund'), dataIndex: 'refundAmount', key: 'refund', width: 120,
                    render: (v) => <Text strong style={{ color: '#ff4d4f' }}>{record.currency === 'IQD' ? `IQD ${v?.toLocaleString()}` : `$${v?.toFixed(2)}`}</Text>
                },
                {
                    title: t('pharmacy:returns.reason'), dataIndex: 'reason', key: 'reason', width: 130,
                    render: (r) => <Tag>{t(`pharmacy:common.returnReasons.${r}`, r)}</Tag>
                }
            ]}
        />
    );

    return (
        <div style={{ padding: 24 }}>
            <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                <Title level={2} style={{ margin: 0 }}><RollbackOutlined /> {t('pharmacy:returns.title')}</Title>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    size="large"
                    onClick={() => { resetModal(); setModalVisible(true); }}
                >
                    {t('pharmacy:returns.newReturn')}
                </Button>
            </Row>

            {/* Stats */}
            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col xs={12} sm={8} lg={6}>
                    <Card size="small">
                        <Statistic
                            title={t('pharmacy:returns.totalReturns')}
                            value={filteredReturns.length}
                            prefix={<RollbackOutlined />}
                            valueStyle={{ color: '#ff4d4f' }}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={8} lg={6}>
                    <Card size="small">
                        <Statistic
                            title={t('pharmacy:returns.totalRefunded')}
                            value={totalRefunded}
                            prefix={<DollarOutlined />}
                            valueStyle={{ color: '#ff4d4f' }}
                            precision={2}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Filters */}
            <Card size="small" style={{ marginBottom: 16 }}>
                <Row gutter={[12, 12]} align="middle">
                    <Col xs={24} sm={8} lg={6}>
                        <Input
                            placeholder={t('pharmacy:returns.searchPlaceholder')}
                            prefix={<SearchOutlined />}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            allowClear
                        />
                    </Col>
                    <Col xs={24} sm={8} lg={6}>
                        <RangePicker
                            style={{ width: '100%' }}
                            onChange={(dates) => setDateRange(dates)}
                            value={dateRange}
                        />
                    </Col>
                </Row>
            </Card>

            {/* Returns Table */}
            <Card>
                <Table
                    dataSource={filteredReturns}
                    columns={columns}
                    rowKey="_id"
                    loading={loading}
                    size="small"
                    expandable={{ expandedRowRender }}
                    pagination={{
                        ...pagination,
                        showSizeChanger: false,
                        showTotal: (total) => t('pharmacy:returns.totalCount', { total }),
                        onChange: (page) => fetchReturns(page)
                    }}
                    scroll={{ x: 1100 }}
                />
            </Card>

            {/* New Return Modal */}
            <Modal
                title={t('pharmacy:returns.processReturn')}
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                width={800}
                footer={null}
                destroyOnClose
            >
                {/* Sale Lookup */}
                <div style={{ marginBottom: 16 }}>
                    <Text strong style={{ display: 'block', marginBottom: 4 }}>{t('pharmacy:returns.findSaleInvoice')}</Text>
                    <Space.Compact style={{ width: '100%' }}>
                        <Input
                            placeholder={t('pharmacy:returns.enterInvoice')}
                            value={saleSearch}
                            onChange={(e) => setSaleSearch(e.target.value)}
                            onPressEnter={lookupSale}
                            prefix={<SearchOutlined />}
                            style={{ flex: 1 }}
                        />
                        <Button type="primary" onClick={lookupSale} loading={saleLoading}>
                            {t('pharmacy:returns.lookup')}
                        </Button>
                    </Space.Compact>
                </div>

                {foundSale && (
                    <>
                        {/* Sale Info */}
                        <Card size="small" style={{ marginBottom: 16, background: '#f6ffed' }}>
                            <Row gutter={16}>
                                <Col span={8}>
                                    <Text type="secondary">{t('pharmacy:returns.invoiceLabel')}</Text>{' '}
                                    <Text strong>{foundSale.saleNumber}</Text>
                                </Col>
                                <Col span={8}>
                                    <Text type="secondary">{t('pharmacy:returns.dateLabel')}</Text>{' '}
                                    <Text>{dayjs(foundSale.saleDate).format('YYYY-MM-DD')}</Text>
                                </Col>
                                <Col span={8}>
                                    <Text type="secondary">{t('pharmacy:returns.totalLabel')}</Text>{' '}
                                    <Text strong>
                                        {foundSale.currency === 'IQD'
                                            ? `IQD ${foundSale.totalAmount?.toLocaleString()}`
                                            : `$${foundSale.totalAmount?.toFixed(2)}`}
                                    </Text>
                                </Col>
                            </Row>
                            {foundSale.patientId && (
                                <Row style={{ marginTop: 4 }}>
                                    <Text type="secondary">{t('pharmacy:returns.patientLabel')}</Text>{' '}
                                    <Text>{foundSale.patientId?.name || '-'}</Text>
                                </Row>
                            )}
                        </Card>

                        {/* Items to return */}
                        <Text strong style={{ display: 'block', marginBottom: 8 }}>{t('pharmacy:returns.selectItems')}</Text>
                        <Table
                            dataSource={returnItems}
                            rowKey="pharmacyItemId"
                            pagination={false}
                            size="small"
                            style={{ marginBottom: 16 }}
                            columns={[
                                {
                                    title: t('pharmacy:common.item'),
                                    dataIndex: 'itemName',
                                    key: 'itemName'
                                },
                                {
                                    title: t('pharmacy:returns.soldQty'),
                                    dataIndex: 'maxQty',
                                    key: 'maxQty',
                                    width: 80,
                                    align: 'center'
                                },
                                {
                                    title: t('pharmacy:returns.returnQty'),
                                    key: 'quantityReturned',
                                    width: 100,
                                    render: (_, record, index) => (
                                        <InputNumber
                                            min={0}
                                            max={record.maxQty}
                                            value={record.quantityReturned}
                                            size="small"
                                            onChange={(val) => updateReturnItem(index, 'quantityReturned', val || 0)}
                                        />
                                    )
                                },
                                {
                                    title: t('pharmacy:common.unitPrice'),
                                    dataIndex: 'unitPrice',
                                    key: 'unitPrice',
                                    width: 100,
                                    render: (v) => foundSale.currency === 'IQD' ? `IQD ${v?.toLocaleString()}` : `$${v?.toFixed(2)}`
                                },
                                {
                                    title: t('pharmacy:returns.refund'),
                                    key: 'refund',
                                    width: 110,
                                    render: (_, record) => {
                                        const amount = record.quantityReturned * record.unitPrice;
                                        return <Text strong style={{ color: amount > 0 ? '#ff4d4f' : undefined }}>
                                            {foundSale.currency === 'IQD' ? `IQD ${amount.toLocaleString()}` : `$${amount.toFixed(2)}`}
                                        </Text>;
                                    }
                                },
                                {
                                    title: t('pharmacy:returns.reason'),
                                    key: 'reason',
                                    width: 150,
                                    render: (_, record, index) => (
                                        <Select
                                            size="small"
                                            value={record.reason}
                                            onChange={(val) => updateReturnItem(index, 'reason', val)}
                                            style={{ width: '100%' }}
                                        >
                                            {REASONS.map(r => (
                                                <Select.Option key={r} value={r}>{t(`pharmacy:common.returnReasons.${r}`, r)}</Select.Option>
                                            ))}
                                        </Select>
                                    )
                                }
                            ]}
                        />

                        <Row gutter={16} style={{ marginBottom: 16 }}>
                            <Col span={8}>
                                <Text strong style={{ display: 'block', marginBottom: 4 }}>{t('pharmacy:returns.refundMethod')}</Text>
                                <Select value={refundMethod} onChange={setRefundMethod} style={{ width: '100%' }}>
                                    <Select.Option value="Cash">{t('pharmacy:common.paymentMethods.Cash')}</Select.Option>
                                    <Select.Option value="Credit Card">{t('pharmacy:common.paymentMethods.Credit Card')}</Select.Option>
                                    <Select.Option value="Store Credit">{t('pharmacy:returns.storeCredit')}</Select.Option>
                                    <Select.Option value="Original Method">{t('pharmacy:returns.originalMethod')}</Select.Option>
                                </Select>
                            </Col>
                            <Col span={16}>
                                <Text strong style={{ display: 'block', marginBottom: 4 }}>{t('pharmacy:common.notes')}</Text>
                                <Input.TextArea
                                    rows={1}
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    placeholder={t('pharmacy:returns.notesPlaceholder')}
                                />
                            </Col>
                        </Row>

                        <Divider />

                        <Row justify="space-between" align="middle">
                            <Text strong style={{ fontSize: 18, color: '#ff4d4f' }}>
                                {t('pharmacy:returns.totalRefundLabel')}: {foundSale.currency === 'IQD'
                                    ? `IQD ${totalRefund.toLocaleString()}`
                                    : `$${totalRefund.toFixed(2)}`}
                            </Text>
                            <Button
                                type="primary"
                                danger
                                size="large"
                                icon={<RollbackOutlined />}
                                onClick={handleSubmitReturn}
                                loading={saving}
                                disabled={!hasReturnItems}
                            >
                                {t('pharmacy:returns.processReturn')}
                            </Button>
                        </Row>
                    </>
                )}
            </Modal>
        </div>
    );
};

export default Returns;
